import React, { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import Cookies from "js-cookie";
import config from "../coreFIles/config";
import { adminLogin, adminSetupMfa, adminVerifyMfa } from "../Action/action";

// Cognito invalidates the challenge session a few minutes after it is issued.
const SESSION_SECONDS = 170;

const Login = () => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [validationError, setValidationError] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  // "credentials" -> "setup" (first sign-in) or "mfa" (authenticator enrolled)
  const [step, setStep] = useState("credentials");
  const [session, setSession] = useState("");
  const [enrollment, setEnrollment] = useState(null);
  const [code, setCode] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);

  const resetToCredentials = () => {
    setStep("credentials");
    setSession("");
    setEnrollment(null);
    setCode("");
    setSecondsLeft(0);
    setIsDisabled(false);
    setForm((prev) => ({ ...prev, password: "" }));
  };

  useEffect(() => {
    if (step === "credentials" || secondsLeft <= 0) {
      return;
    }

    const timer = setTimeout(() => setSecondsLeft((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [step, secondsLeft]);

  useEffect(() => {
    if (step !== "credentials" && secondsLeft === 0 && session) {
      toast.error("Your session expired, please sign in again");
      resetToCredentials();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, secondsLeft, session]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setValidationError((prevErrors) => ({
      ...prevErrors,
      [`${name}Error`]: "",
    }));
    setIsDisabled(false);
  };

  const handleCodeChange = (e) => {
    setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
    setValidationError((prevErrors) => ({ ...prevErrors, codeError: "" }));
    setIsDisabled(false);
  };

  const validate = () => {
    const errors = {};
    let isValid = true;

    if (!form.email.trim()) {
      errors.emailError = "Email is required.";
      isValid = false;
    } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      errors.emailError = "Please enter a valid email address.";
      isValid = false;
    }

    if (!form.password) {
      errors.passwordError = "Password is required.";
      isValid = false;
    }

    setValidationError(errors);
    return isValid;
  };

  const completeLogin = (res) => {
    Cookies.set("OnchainMatrix", JSON.stringify(res.data.token));
    toast.success(res.msg);
    setTimeout(() => {
      window.location.href = `${config.baseUrl}seed-round`;
    }, 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsDisabled(true);

    if (!validate()) {
      setIsDisabled(false);
      return;
    }

    try {
      toast.dismiss();
      const res = await adminLogin(form);

      if (!res.status) {
        toast.error(res.msg);
        setIsDisabled(false);
        return;
      }

      if (res.data?.mfaSetupRequired) {
        setSession(res.data.session);
        setEnrollment({
          qrImage: res.data.qrImage,
          secretCode: res.data.secretCode,
        });
        setStep("setup");
        setSecondsLeft(SESSION_SECONDS);
        setIsDisabled(false);
        return;
      }

      if (res.data?.mfaRequired) {
        setSession(res.data.session);
        setStep("mfa");
        setSecondsLeft(SESSION_SECONDS);
        setIsDisabled(false);
        return;
      }

      completeLogin(res);
    } catch (error) {
      if (error?.response?.status === 401) {
        toast.error("Invalid email or password");
        setIsDisabled(false);
        return;
      }

      toast.error("Something went wrong");
      setIsDisabled(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setIsDisabled(true);

    if (code.length !== 6) {
      setValidationError({ codeError: "Enter the 6-digit code." });
      setIsDisabled(false);
      return;
    }

    try {
      toast.dismiss();
      const payload = { email: form.email.trim(), session, code };
      const res =
        step === "setup"
          ? await adminSetupMfa(payload)
          : await adminVerifyMfa(payload);

      if (!res.status) {
        toast.error(res.msg);
        setCode("");
        setIsDisabled(false);
        return;
      }

      completeLogin(res);
    } catch (error) {
      toast.error("Something went wrong");
      setCode("");
      setIsDisabled(false);
    }
  };

  const togglePasswordVisibility = (e) => {
    e.preventDefault();
    setShowPassword(!showPassword);
  };

  const formatCountdown = () => {
    const minutes = Math.floor(secondsLeft / 60);
    const seconds = String(secondsLeft % 60).padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const renderCredentials = () => (
    <>
      <div className="content-top-agile p-20 pb-0">
        <center className="loginLogo">
          <img
            src="images/logo/logo_new.png"
            alt="logo"
            style={{ width: "100px" }}
          />
        </center>

        <h2 className="text-white" style={{ fontWeight: "600" }}>
          Admin Panel
        </h2>
        <p className="mb-0">Sign in to continue to OnchainMatrix Admin.</p>
      </div>
      <div className="p-40">
        <form onSubmit={handleSubmit}>
          <div className="form-group mb-3">
            <div className="input-group ">
              <span className="input-group-text">
                <i className="ti-user" />
              </span>
              <input
                type="email"
                className={`form-control ps-15 ${validationError.emailError ? "is-invalid" : ""}`}
                placeholder="Please enter email"
                onChange={handleInputChange}
                name="email"
                autoComplete="new-email"
                value={form.email}
              />
            </div>
            {validationError.emailError && (
              <div className="text-danger small">
                {validationError.emailError}
              </div>
            )}
          </div>
          <div className="form-group mb-3 ">
            <div className="input-group passfield">
              <span
                className="input-group-text py-2"
                style={{ minHeight: "38px" }}
              >
                <i className="ti-lock" />
              </span>
              <input
                type={showPassword ? "text" : "password"}
                className={`form-control ps-15 ${validationError.passwordError ? "is-invalid" : ""}`}
                placeholder="Please enter password"
                onChange={handleInputChange}
                name="password"
                autoComplete="new-password"
                value={form.password}
              />
              <button
                className="input-group-text password-toggle"
                onClick={togglePasswordVisibility}
                type="button"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <i className={`fa ${showPassword ? "fa-eye" : "fa-eye-slash"}`} />
              </button>
            </div>
            {validationError.passwordError && (
              <div className="text-danger small">
                {validationError.passwordError}
              </div>
            )}
          </div>

          <div className="row">
            <div className="col-12 text-center">
              <button
                type="submit"
                style={{ border: "1px solid #e1b02880" }}
                className="btn btn-primary mt-10"
                disabled={isDisabled}
              >
                {isDisabled ? "Signing in..." : "SIGN IN"}
              </button>
            </div>
            <div className="col-12 text-center mt-3">
              <a href={`${config.baseUrl}forget-password`}>Forgot password?</a>
            </div>
          </div>
        </form>
      </div>
    </>
  );

  const renderCodeStep = () => (
    <>
      <div className="content-top-agile p-20 pb-0">
        <center className="loginLogo">
          <img
            src="images/logo/logo_new.png"
            alt="logo"
            style={{ width: "100px" }}
          />
        </center>

        <h2 className="text-white" style={{ fontWeight: "600" }}>
          {step === "setup" ? "Set up two-factor" : "Two-factor code"}
        </h2>
        <p className="mb-0">
          {step === "setup"
            ? "Scan the QR with Google Authenticator, then enter the code it shows."
            : "Enter the 6-digit code from your authenticator app."}
        </p>
      </div>
      <div className="p-40">
        {step === "setup" && enrollment && (
          <div className="text-center mb-3">
            <img
              src={enrollment.qrImage}
              alt="Two-factor QR code"
              style={{
                width: "190px",
                background: "#fff",
                padding: "8px",
                borderRadius: "8px",
              }}
            />
            <p className="small mt-2 mb-1">Can't scan? Enter this key manually:</p>
            <code
              style={{ wordBreak: "break-all", fontSize: "12px" }}
              className="text-white"
            >
              {enrollment.secretCode}
            </code>
          </div>
        )}

        <form onSubmit={handleCodeSubmit}>
          <div className="form-group mb-3">
            <div className="input-group">
              <span className="input-group-text">
                <i className="ti-mobile" />
              </span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                className={`form-control ps-15 ${validationError.codeError ? "is-invalid" : ""}`}
                placeholder="000000"
                style={{ letterSpacing: "6px" }}
                onChange={handleCodeChange}
                name="code"
                value={code}
                autoFocus
              />
            </div>
            {validationError.codeError && (
              <div className="text-danger small">
                {validationError.codeError}
              </div>
            )}
          </div>

          {secondsLeft > 0 && (
            <p className="small text-center mb-2">
              This step expires in {formatCountdown()}
            </p>
          )}

          <div className="row">
            <div className="col-12 text-center">
              <button
                type="submit"
                style={{ border: "1px solid #e1b02880" }}
                className="btn btn-primary mt-10"
                disabled={isDisabled}
              >
                {isDisabled ? "Verifying..." : "VERIFY"}
              </button>
            </div>
            <div className="col-12 text-center mt-3">
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={resetToCredentials}
              >
                Back to sign in
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );

  return (
    <div className="hold-transition theme-primary bg-img">
      <div className="container h-p100">
        <Toaster />
        <div className="row align-items-center justify-content-md-center h-p100">
          <div className="col-12">
            <div className="row justify-content-center g-0">
              <div className="col-lg-5 col-md-5 col-12">
                <div className="rounded10 shadow-lg admin-login">
                  {step === "credentials"
                    ? renderCredentials()
                    : renderCodeStep()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
